/**
 * Movement ledger — `/inventory/movements`.
 *
 * The append-only `stock_movements` fact table, newest first, over a keyset
 * cursor. Read-only by construction: a correction is a new movement, so no
 * edit or delete affordance exists anywhere on this screen. Arriving with
 * `?variant_id=&warehouse_id=` makes it the per-position history drill-in;
 * arriving with `?transfer_id=` shows both legs of one transfer.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { DataTable, badgeCell, dateTimeCell, textCell } from "@/components/data-table";
import { formatDateTime } from "@/lib/format";

import {
  MOVEMENT_SOURCE_TYPES,
  MOVEMENT_TYPES,
  type StockMovement,
  type StockMovementSourceType,
} from "../api";
import {
  useMovementDetail,
  useMovementList,
  useWarehouseOptions,
  type MovementFilters,
} from "../queries";
import { AmountText, SignedQuantityText, UnitCostText } from "../components/decimal-text";
import { ALL, LocationSelect } from "../components/pickers";

const MOVEMENT_BADGE_VARIANTS = {
  receipt: "success",
  issue: "destructive",
  transfer_out: "warning",
  transfer_in: "secondary",
  adjustment: "outline",
  count_variance: "outline",
  revaluation: "default",
} as const;

export interface MovementsPageProps {
  initialVariantId?: string;
  initialWarehouseId?: string;
  initialLocationId?: string;
  initialTransferId?: string;
  initialBatchId?: string;
  /** Document drill-in: every movement one source document produced. */
  initialSourceType?: StockMovementSourceType;
  initialSourceId?: string;
  /** Transfer chip → transfer detail. */
  onOpenTransfer?: (transferId: string) => void;
  /** Deep link back into the position this ledger belongs to. */
  onOpenStock?: (filters: { variantId: string; warehouseId: string }) => void;
  canReadTransfers?: boolean;
  canReadStock?: boolean;
  pageLimit?: number;
}

export function MovementsPage({
  initialVariantId,
  initialWarehouseId,
  initialLocationId,
  initialTransferId,
  initialBatchId,
  initialSourceType,
  initialSourceId,
  onOpenTransfer,
  onOpenStock,
  canReadTransfers = false,
  canReadStock = false,
  pageLimit,
}: MovementsPageProps) {
  const { t, i18n } = useTranslation();

  const [warehouseId, setWarehouseId] = useState<string | null>(initialWarehouseId ?? null);
  const [locationId, setLocationId] = useState<string | null>(initialLocationId ?? null);
  const [movementType, setMovementType] = useState<string>(ALL);
  const [sourceType, setSourceType] = useState<string>(initialSourceType ?? ALL);
  const [occurredFrom, setOccurredFrom] = useState("");
  const [occurredTo, setOccurredTo] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (initialWarehouseId !== undefined) setWarehouseId(initialWarehouseId);
    if (initialLocationId !== undefined) setLocationId(initialLocationId);
  }, [initialWarehouseId, initialLocationId]);

  const warehouses = useWarehouseOptions().data ?? [];

  const filters = useMemo<MovementFilters>(() => {
    const built: MovementFilters = {};
    if (initialVariantId !== undefined) built.variant_id = initialVariantId;
    if (initialTransferId !== undefined) built.transfer_id = initialTransferId;
    if (initialBatchId !== undefined) built.batch_id = initialBatchId;
    if (initialSourceId !== undefined) built.source_id = initialSourceId;
    if (warehouseId !== null) built.warehouse_id = warehouseId;
    if (locationId !== null) built.location_id = locationId;
    if (movementType !== ALL)
      built.movement_type = movementType as MovementFilters["movement_type"];
    if (sourceType !== ALL) built.source_type = sourceType as MovementFilters["source_type"];
    if (occurredFrom !== "") built.occurred_from = new Date(occurredFrom).toISOString();
    if (occurredTo !== "") built.occurred_to = new Date(occurredTo).toISOString();
    return built;
  }, [
    initialVariantId,
    initialTransferId,
    initialBatchId,
    initialSourceId,
    warehouseId,
    locationId,
    movementType,
    sourceType,
    occurredFrom,
    occurredTo,
  ]);

  const query = useMovementList(filters, pageLimit);
  const detail = useMovementDetail(selectedId);

  const columns = useMemo<ColumnDef<StockMovement>[]>(
    () => [
      {
        accessorKey: "occurred_at",
        id: "occurred_at",
        header: t("inventory.movements.columns.occurredAt", "Occurred"),
        enableSorting: false,
        cell: (ctx) => dateTimeCell(ctx.getValue<string>()),
      },
      {
        accessorKey: "movement_type",
        id: "movement_type",
        header: t("inventory.movements.columns.type", "Type"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<string>();
          return badgeCell(
            value,
            MOVEMENT_BADGE_VARIANTS,
            t(`inventory.movementType.${value}`, value),
          );
        },
      },
      {
        accessorKey: "sku",
        id: "sku",
        header: t("inventory.stock.columns.sku", "SKU"),
        enableSorting: false,
        cell: (ctx) => (
          <span dir="ltr" className="font-mono">
            {ctx.getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: "warehouse_code",
        id: "warehouse",
        header: t("inventory.common.warehouse", "Warehouse"),
        enableSorting: false,
        cell: (ctx) => textCell(ctx.getValue<string>()),
      },
      {
        accessorKey: "location_code",
        id: "location",
        header: t("inventory.common.location", "Bin"),
        enableSorting: false,
        cell: (ctx) => textCell(ctx.getValue<string>()),
      },
      {
        accessorKey: "qty",
        id: "qty",
        header: t("inventory.common.qty", "Qty"),
        enableSorting: false,
        meta: { numeric: true },
        cell: (ctx) => <SignedQuantityText value={ctx.getValue<string>()} />,
      },
      {
        accessorKey: "unit_cost",
        id: "unit_cost",
        header: t("inventory.common.unitCost", "Unit cost"),
        enableSorting: false,
        meta: { numeric: true },
        cell: (ctx) => <UnitCostText value={ctx.getValue<string>()} />,
      },
      {
        accessorKey: "value",
        id: "value",
        header: t("inventory.common.value", "Value"),
        enableSorting: false,
        meta: { numeric: true },
        cell: (ctx) => <AmountText value={ctx.getValue<string>()} />,
      },
      {
        accessorKey: "source_type",
        id: "source_type",
        header: t("inventory.movements.columns.source", "Source"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<string>();
          return <Badge variant="outline">{t(`inventory.sourceType.${value}`, value)}</Badge>;
        },
      },
      {
        accessorKey: "batch_no",
        id: "batch",
        header: t("inventory.common.batch", "Batch"),
        enableSorting: false,
        cell: (ctx) => textCell(ctx.getValue<string | undefined>()),
      },
      {
        accessorKey: "transfer_id",
        id: "transfer",
        header: t("inventory.movements.columns.transfer", "Transfer"),
        enableSorting: false,
        cell: (ctx) => {
          const transferId = ctx.getValue<string | undefined>();
          if (transferId === undefined) return textCell(undefined);
          if (!canReadTransfers || onOpenTransfer === undefined) {
            return <Badge variant="secondary">{t("inventory.movements.linked", "Linked")}</Badge>;
          }
          return (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0"
              onClick={(event) => {
                event.stopPropagation();
                onOpenTransfer(transferId);
              }}
            >
              {t("inventory.movements.openTransfer", "Open transfer")}
            </Button>
          );
        },
      },
    ],
    [t, canReadTransfers, onOpenTransfer],
  );

  const selected = detail.data ?? null;

  return (
    <div className="flex flex-col gap-4 p-4">
      <div>
        <h1 className="text-xl font-semibold">
          {t("inventory.movements.title", "Movement ledger")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(
            "inventory.movements.subtitle",
            "Append-only stock facts. Corrections are new movements — nothing here can be edited or deleted.",
          )}
        </p>
      </div>

      {initialVariantId !== undefined ||
      initialTransferId !== undefined ||
      initialSourceId !== undefined ? (
        <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 p-3 text-sm">
          <span className="text-muted-foreground">
            {t("inventory.movements.scoped", "Scoped view")}
          </span>
          {initialVariantId !== undefined ? (
            <Badge variant="outline">{t("inventory.movements.oneVariant", "One SKU")}</Badge>
          ) : null}
          {initialTransferId !== undefined ? (
            <Badge variant="outline">
              {t("inventory.movements.bothLegs", "Both legs of one transfer")}
            </Badge>
          ) : null}
          {initialSourceId !== undefined ? (
            <Badge variant="outline">
              {t("inventory.movements.oneDocument", "One source document")}
            </Badge>
          ) : null}
          {canReadStock && onOpenStock !== undefined && initialVariantId !== undefined ? (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0"
              onClick={() =>
                onOpenStock({
                  variantId: initialVariantId,
                  warehouseId: warehouseId ?? initialWarehouseId ?? "",
                })
              }
            >
              {t("inventory.movements.backToStock", "Back to the position")}
            </Button>
          ) : null}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="movement-warehouse">{t("inventory.common.warehouse", "Warehouse")}</Label>
          <Select
            value={warehouseId ?? ALL}
            onValueChange={(value) => {
              setWarehouseId(value === ALL ? null : value);
              setLocationId(null);
            }}
          >
            <SelectTrigger id="movement-warehouse">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("inventory.common.all", "All")}</SelectItem>
              {warehouses.map((warehouse) => (
                <SelectItem key={warehouse.id} value={warehouse.id}>
                  {warehouse.code} — {warehouse.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {warehouseId !== null ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="movement-location">{t("inventory.common.location", "Bin")}</Label>
            <LocationSelect
              id="movement-location"
              warehouseId={warehouseId}
              value={locationId}
              includeAll
              onChange={setLocationId}
            />
          </div>
        ) : null}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="movement-type">{t("inventory.movements.columns.type", "Type")}</Label>
          <Select value={movementType} onValueChange={setMovementType}>
            <SelectTrigger id="movement-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("inventory.common.all", "All")}</SelectItem>
              {MOVEMENT_TYPES.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`inventory.movementType.${value}`, value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="movement-source">
            {t("inventory.movements.columns.source", "Source")}
          </Label>
          <Select value={sourceType} onValueChange={setSourceType}>
            <SelectTrigger id="movement-source">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("inventory.common.all", "All")}</SelectItem>
              {MOVEMENT_SOURCE_TYPES.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`inventory.sourceType.${value}`, value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="movement-from">{t("inventory.movements.from", "From")}</Label>
          <Input
            id="movement-from"
            type="datetime-local"
            value={occurredFrom}
            onChange={(event) => setOccurredFrom(event.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="movement-to">{t("inventory.movements.to", "To")}</Label>
          <Input
            id="movement-to"
            type="datetime-local"
            value={occurredTo}
            onChange={(event) => setOccurredTo(event.target.value)}
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        query={query}
        getRowId={(row) => row.id}
        onRowClick={(row) => setSelectedId(row.id)}
        storageKey="inventory.movements.list.columns"
        emptyMessage={t("inventory.movements.empty", "No movements recorded here yet.")}
        errorMessage={t("inventory.movements.loadError", "Movements could not be loaded.")}
      />

      {selectedId !== null ? (
        <Card>
          <CardHeader className="flex-row items-center justify-between gap-3">
            <CardTitle>
              <h2>{t("inventory.movements.detailTitle", "Movement detail")}</h2>
            </CardTitle>
            <Button type="button" variant="ghost" size="sm" onClick={() => setSelectedId(null)}>
              {t("actions.close")}
            </Button>
          </CardHeader>
          <CardContent>
            {detail.isError ? (
              <p role="alert" className="text-sm text-destructive">
                {t("inventory.movements.detailError", "This movement could not be loaded.")}
              </p>
            ) : selected === null ? (
              <p className="text-sm text-muted-foreground">
                {t("inventory.common.loading", "Loading…")}
              </p>
            ) : (
              <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <dt className="text-muted-foreground">
                    {t("inventory.movements.columns.type", "Type")}
                  </dt>
                  <dd>
                    {t(`inventory.movementType.${selected.movement_type}`, selected.movement_type)}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">{t("inventory.common.qty", "Qty")}</dt>
                  <dd>
                    <SignedQuantityText value={selected.qty} />
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">
                    {t("inventory.common.unitCost", "Unit cost")}
                  </dt>
                  <dd>
                    <UnitCostText value={selected.unit_cost} />
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">{t("inventory.common.value", "Value")}</dt>
                  <dd>
                    <AmountText value={selected.value} />
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">
                    {t("inventory.movements.columns.occurredAt", "Occurred")}
                  </dt>
                  <dd>{formatDateTime(selected.occurred_at, i18n.language)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">
                    {t("inventory.movements.createdAt", "Recorded")}
                  </dt>
                  <dd>{formatDateTime(selected.created_at, i18n.language)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">
                    {t("inventory.movements.columns.source", "Source")}
                  </dt>
                  <dd>{t(`inventory.sourceType.${selected.source_type}`, selected.source_type)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">
                    {t("inventory.movements.position", "Position")}
                  </dt>
                  <dd>
                    <span dir="ltr" className="font-mono">
                      {selected.sku}
                    </span>
                    {" · "}
                    {selected.warehouse_code}
                    {" · "}
                    {selected.location_code}
                  </dd>
                </div>
                {selected.reversal_of_movement_id !== undefined ? (
                  <div>
                    <dt className="text-muted-foreground">
                      {t("inventory.movements.reversalOf", "Reverses")}
                    </dt>
                    <dd>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-auto p-0"
                        onClick={() => setSelectedId(selected.reversal_of_movement_id ?? null)}
                      >
                        {t("inventory.movements.openOriginal", "Open the original movement")}
                      </Button>
                    </dd>
                  </div>
                ) : null}
                {selected.batch_no !== undefined ? (
                  <div>
                    <dt className="text-muted-foreground">
                      {t("inventory.common.batch", "Batch")}
                    </dt>
                    <dd>{selected.batch_no}</dd>
                  </div>
                ) : null}
                <div>
                  <dt className="text-muted-foreground">
                    {t("inventory.movements.branch", "Branch")}
                  </dt>
                  <dd>{selected.branch_code}</dd>
                </div>
              </dl>
            )}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
